import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import { adminUserListItemSchema, type AdminUserListItem } from '@steado/admin-contract/users';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

export interface UsersFilters {
  readonly search?: string | undefined;
  readonly status?: string | undefined;
  readonly plan?: string | undefined;
  readonly diet?: string | undefined;
  readonly onboarded?: string | undefined;
  readonly platform?: string | undefined;
  readonly gym?: string | undefined;
  readonly cursor?: string | undefined;
}

export interface UsersPage {
  readonly items: AdminUserListItem[];
  readonly pagination: PaginationMeta;
}

/** The list endpoint answers with the array in `data` and paging in `meta`. */
const usersResponseSchema = z.object({
  items: z.array(adminUserListItemSchema),
  pagination: paginationMetaSchema,
});

export const USERS_PAGE_SIZE = 20;

export function usersQueryOptions(api: ApiClient, filters: UsersFilters) {
  const query: Record<string, QueryValue> = {
    search: filters.search,
    status: filters.status,
    plan: filters.plan,
    diet: filters.diet,
    onboarded: filters.onboarded,
    platform: filters.platform,
    gym: filters.gym,
    cursor: filters.cursor,
    limit: USERS_PAGE_SIZE,
  };

  return queryOptions({
    // Every filter is part of the key, so changing one is a different cache
    // entry rather than a refetch that briefly shows the wrong rows.
    queryKey: ['admin', 'users', filters],
    queryFn: async (): Promise<UsersPage> => {
      /**
       * The client validates `data` against its schema and hands back `meta`
       * separately, so the two are stitched here. Parsing the pair together
       * keeps an endpoint that forgets its pagination meta from reaching the UI.
       */
      const response = await api.requestWithMeta({
        path: '/admin/users',
        query,
        schema: z.array(adminUserListItemSchema),
      });

      return usersResponseSchema.parse({
        items: response.data,
        pagination: response.meta.pagination,
      });
    },
    // Keeps the previous page on screen while the next loads, instead of
    // flashing an empty table between pages.
    placeholderData: (previous) => previous,
  });
}
